import { useState } from "react";
import { Bell, Shield, Menu, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavigate, useLocation } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useReminderCount } from "@/hooks/useInvoiceReminders";
import { NotificationPanel } from "./NotificationPanel";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserPlan } from "@/hooks/useUserPlan";
import { PlanComparisonModal } from "@/components/PlanComparisonModal";
import { usePageTitleContext } from "@/contexts/PageTitleContext";
import { AskCatalystFlowButton } from "@/components/catalyst-flow/AskCatalystFlowButton";

// Contextual upgrade messages based on current route
const upgradeMessages: Record<string, string> = {
  "/dashboard": "Unlock deeper insights with AI Strategy",
  "/finance": "Forecast your next 30 days with AI",
  "/sales": "Get AI-powered deal recommendations",
  "/tasks": "Smart task prioritization with AI",
  "/invoices": "Automated payment reminders & forecasts",
  "/inbox": "AI-powered workload optimization",
  "/calendar": "Smart scheduling & conflict detection",
  "/projects-clients": "Client health insights with AI",
  "/settings": "Unlock premium features",
};

interface TopBarProps {
  onMobileMenuToggle: () => void;
}

export const TopBar = ({ onMobileMenuToggle }: TopBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAdminAuth();
  const { data: reminderCount = 0 } = useReminderCount();
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const isMobile = useIsMobile();
  const { currentPlan, loading: planLoading } = useUserPlan();
  const { pageTitle, pageSubtitle } = usePageTitleContext();

  // Check if user should see upgrade CTA
  const planType = currentPlan?.planType?.toLowerCase();
  const showUpgradeCTA = !planLoading && planType !== 'pro_ai';

  // Get contextual upgrade message based on current route
  const currentPath = location.pathname;
  const upgradeMessage = upgradeMessages[currentPath] || "Unlock AI Strategy & Smart Forecast";

  return (
    <header className="flex h-16 items-center justify-between border-b bg-card px-3 sm:px-6 shadow-sm">
      <div className="flex items-center gap-3">
        {/* Hamburger menu for mobile */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMobileMenuToggle}
        >
          <Menu className="h-5 w-5" />
        </Button>
        
        {/* Dynamic page title - desktop only */}
        {!isMobile && pageTitle && (
          <div>
            <h2 className="text-lg font-semibold text-foreground">{pageTitle}</h2>
            {pageSubtitle && (
              <p className="text-sm text-muted-foreground">{pageSubtitle}</p>
            )}
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Ask Catalyst Flow Button */}
        <AskCatalystFlowButton />
        
        {/* Upgrade CTA - Only show for non-pro users */}
        {showUpgradeCTA && !isMobile && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPlanModal(true)}
                  className="gap-2 border-primary bg-primary/5 text-primary hover:bg-primary/15 hover:text-primary shadow-sm shadow-primary/20"
                >
                  <Crown className="h-4 w-4" />
                  <span className="hidden md:inline">Upgrade to CORE</span>
                  <span className="md:hidden">Upgrade</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="text-xs">{upgradeMessage}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {showUpgradeCTA && isMobile && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPlanModal(true)}
                  className="text-primary bg-primary/5"
                >
                  <Crown className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{upgradeMessage}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative"
                onClick={() => setNotificationPanelOpen(true)}
              >
                <Bell className="h-5 w-5" />
                {reminderCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -right-1 -top-1 h-5 min-w-5 px-1 text-xs flex items-center justify-center"
                  >
                    {reminderCount}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Invoice Reminders {reminderCount > 0 && `(${reminderCount})`}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {isAdmin && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate('/admin')}
                >
                  <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Admin Panel</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <NotificationPanel 
        open={notificationPanelOpen} 
        onOpenChange={setNotificationPanelOpen}
      />

      <PlanComparisonModal 
        open={showPlanModal} 
        onOpenChange={setShowPlanModal}
        currentPlanType={currentPlan?.planType}
      />
    </header>
  );
};
